// ============================================================================
// checklistBookService — CRUD + lookahead for the three per-workspace books.
// ----------------------------------------------------------------------------
// Owns:
//   - Book bootstrap (create the three books when a workspace first uses
//     checklists).
//   - Page pre-creation for DAILY / WEEKLY based on
//     WorkspaceRecurringSettings.recurringWindowDays.
//   - Per-checklist completion writes into a page's `completions` map.
//   - Retention prune (delete pages older than retentionDays).
//   - Read helpers used by the canvas (page + layout + definitions).
//
// Does NOT own:
//   - Layout mutations — see layoutVersionService.
//   - Definition CRUD (title/form/etc.) — see checklistDefinitionService.
//
// See src/types/checklistBooks.ts for the data shapes and rationale.
// ============================================================================

import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
  type Unsubscribe,
} from 'firebase/firestore'
import { firestoreDb } from './firebase'
import { scopedReadConstraints } from './accessScopeService'
import {
  bookIdFor,
  bookPath,
  CHECKLISTS_COL,
  INDIVIDUAL_PAGE_KEY,
  PAGES_SUBCOL,
  pagePath,
  type BookId,
  type ChecklistBook,
  type ChecklistBookPage,
  type ChecklistCompletionState,
  type ChecklistDefinition,
  type ChecklistType,
  type PageKey,
  type RecurringChecklistType,
} from '../types/checklistBooks'
export { bookIdFor }
import type { WorkspaceRecurringSettings } from '../types/recurringChecklist'
import {
  ensureActiveLayoutVersion,
  resolveInheritedVersion,
  type LayoutActor,
} from './layoutVersionService'
import {
  ensureActiveDetailVersion,
  resolveInheritedDetailVersion,
} from './checklistDetailVersionService'
import {
  invalidateChecklistPageCache,
  primeChecklistDefinitionListCache,
  primeChecklistPageCache,
  readCachedChecklistDefinitions,
  readCachedChecklistPage,
} from './checklistReadCache'
import { DEFAULT_RECURRING_WINDOW_DAYS, normalizeRecurringWindowDays } from '../types/recurringChecklist'
import {
  formatZonedDateKey,
  getWeekBucketKey,
  resolveWorkspaceTimeZone,
  startOfIsoWeekDate,
} from './repeat/keys'

// ---------------------------------------------------------------------------
// Actor
// ---------------------------------------------------------------------------

export type BookActor = LayoutActor

// ---------------------------------------------------------------------------
// Date / page-key helpers
// ---------------------------------------------------------------------------

export const isoDate = (date: Date, timeZone?: string): string => {
  if (timeZone) {
    return formatZonedDateKey(date, resolveWorkspaceTimeZone(timeZone))
  }
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
  return formatZonedDateKey(date, tz)
}

export const startOfIsoWeek = (date: Date, timeZone?: string): Date => {
  return startOfIsoWeekDate(date, timeZone)
}

export const isoWeekKey = (date: Date, timeZone?: string): string => {
  const tz = timeZone ? resolveWorkspaceTimeZone(timeZone) : Intl.DateTimeFormat().resolvedOptions().timeZone
  return getWeekBucketKey(date, tz)
}

export const dateFromIsoWeekKey = (weekKey: string): Date => {
  const [yearRaw, weekRaw] = weekKey.split('-W')
  const year = Number(yearRaw)
  const week = Number(weekRaw)
  if (!Number.isFinite(year) || !Number.isFinite(week) || week <= 0) {
    return startOfIsoWeek(new Date())
  }
  const jan4 = new Date(year, 0, 4)
  const monday = startOfIsoWeek(jan4)
  const target = new Date(monday)
  target.setDate(target.getDate() + (week - 1) * 7)
  target.setHours(0, 0, 0, 0)
  return target
}

export const pageKeyFor = (type: ChecklistType, date: Date, timeZone?: string): PageKey => {
  if (type === 'INDIVIDUAL') return INDIVIDUAL_PAGE_KEY
  if (type === 'WEEKLY') return isoWeekKey(date, timeZone)
  return isoDate(date, timeZone)
}

export const pageDateIsoFor = (type: ChecklistType, date: Date, timeZone?: string): string => {
  if (type === 'INDIVIDUAL') return isoDate(date, timeZone)
  if (type === 'WEEKLY') return isoDate(startOfIsoWeek(date, timeZone), timeZone)
  return isoDate(date, timeZone)
}

export const pageDateIsoFromPageKey = (
  type: ChecklistType,
  pageKey: PageKey,
  fallbackDate: Date = new Date(),
): string => {
  if (type === 'INDIVIDUAL') return isoDate(fallbackDate)
  if (type === 'WEEKLY') return isoDate(dateFromIsoWeekKey(pageKey))
  return pageKey
}

export const advanceDate = (type: ChecklistType, date: Date, steps: number): Date => {
  const clone = new Date(date)
  const unit = type === 'WEEKLY' ? 7 : 1
  clone.setDate(clone.getDate() + steps * unit)
  clone.setHours(0, 0, 0, 0)
  return clone
}

/** Iterate the pre-created window: today + (windowSize - 1) upcoming. */
export const enumerateLookaheadPageKeys = (
  type: RecurringChecklistType,
  windowSize: number,
  today: Date = new Date(),
): PageKey[] => {
  const size = Math.max(1, Math.floor(windowSize))
  const keys: PageKey[] = []
  for (let i = 0; i < size; i += 1) {
    const d = advanceDate(type, today, i)
    keys.push(pageKeyFor(type, d))
  }
  return keys
}

// ---------------------------------------------------------------------------
// Firestore refs
// ---------------------------------------------------------------------------

const bookRef = (bookId: BookId) => doc(firestoreDb, bookPath(bookId))
const pageRef = (bookId: BookId, pageKey: PageKey) =>
  doc(firestoreDb, pagePath(bookId, pageKey))
const pagesCollectionRef = (bookId: BookId) =>
  collection(firestoreDb, `${bookPath(bookId)}/${PAGES_SUBCOL}`)
const checklistsCollectionRef = () => collection(firestoreDb, CHECKLISTS_COL)

// ---------------------------------------------------------------------------
// Book CRUD
// ---------------------------------------------------------------------------

export const getBook = async (
  workspaceId: string,
  type: ChecklistType,
): Promise<ChecklistBook | null> => {
  const snap = await getDoc(bookRef(bookIdFor(workspaceId, type)))
  return snap.exists() ? (snap.data() as ChecklistBook) : null
}

/**
 * Ensure a book exists for (workspaceId, type). Creates the book doc AND the
 * initial page (INDIVIDUAL) or the pre-created lookahead window (DAILY /
 * WEEKLY). Idempotent — subsequent calls are cheap no-ops.
 */
export const ensureBook = async (
  workspaceId: string,
  type: ChecklistType,
  actor: BookActor,
  opts: { lookaheadPages?: number } = {},
): Promise<ChecklistBook> => {
  const id = bookIdFor(workspaceId, type)
  const existing = await getBook(workspaceId, type)
  if (existing) {
    const requestedLookaheadPages = type === 'INDIVIDUAL'
      ? undefined
      : (opts.lookaheadPages ?? existing.lookaheadPages ?? DEFAULT_LOOKAHEAD[type])
    if (!existing.activeDetailVersionId) {
      await ensureActiveDetailVersion(id, workspaceId, actor)
    }
    if (type !== 'INDIVIDUAL') {
      if (existing.lookaheadPages !== requestedLookaheadPages) {
        await updateDoc(bookRef(id), {
          lookaheadPages: requestedLookaheadPages,
          updatedAt: serverTimestamp(),
          recordVersion: (existing.recordVersion ?? 0) + 1,
        })
      }
    }
    return {
      ...existing,
      ...(type !== 'INDIVIDUAL' ? { lookaheadPages: requestedLookaheadPages } : {}),
      recordVersion: type !== 'INDIVIDUAL' && existing.lookaheadPages !== requestedLookaheadPages
        ? (existing.recordVersion ?? 0) + 1
        : existing.recordVersion,
    }
  }

  const activeLayoutVersionId = await ensureActiveLayoutVersion(id, workspaceId, actor)
  const activeDetailVersionId = await ensureActiveDetailVersion(id, workspaceId, actor)
  const book: ChecklistBook = {
    id,
    workspaceId,
    checklistType: type,
    activeLayoutVersionId,
    activeDetailVersionId,
    ...(type !== 'INDIVIDUAL'
      ? { lookaheadPages: opts.lookaheadPages ?? DEFAULT_LOOKAHEAD[type] }
      : {}),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    recordVersion: 1,
  }
  await setDoc(bookRef(id), book)

  if (type === 'INDIVIDUAL') {
    await ensurePage(id, workspaceId, type, INDIVIDUAL_PAGE_KEY, isoDate(new Date()), actor)
  }
  return book
}

/** Default pages ahead of today to keep materialised per recurring type.
 *  Overridable per-book via `ChecklistBook.lookaheadPages`, or globally via
 *  WorkspaceRecurringSettings.recurringWindowDays. */
export const DEFAULT_LOOKAHEAD: Record<RecurringChecklistType, number> = {
  DAILY: 3,
  WEEKLY: 3,
}


/**
 * Parent-scoped recurring refactor: this used to materialize a rolling
 * `today + lookaheadPages` window of empty pages on every mount. Under the
 * new model pages are strictly lazy — `ensurePage` fires only when a real
 * write happens (create-node, tick, edit). We keep the function name and
 * shape because ~20 callers still route through it to bootstrap the book
 * doc itself; the materialization side-effect is now a no-op.
 */
export const ensureRecurringBookWindow = async (
  workspaceId: string,
  type: ChecklistType,
  actor: BookActor,
  opts: {
    lookaheadPages?: number
    forceMaterialize?: boolean
  } = {},
): Promise<ChecklistBook> => {
  const book = await ensureBook(workspaceId, type, actor, {
    lookaheadPages: opts.lookaheadPages,
  })
  if (type === 'INDIVIDUAL') return book
  const lookaheadPages = opts.lookaheadPages ?? book.lookaheadPages ?? DEFAULT_LOOKAHEAD[type]
  return {
    ...book,
    lookaheadPages,
  }
}

// ---------------------------------------------------------------------------
// Page CRUD
// ---------------------------------------------------------------------------

export const getPage = async (
  bookId: BookId,
  pageKey: PageKey,
): Promise<ChecklistBookPage | null> => {
  return readCachedChecklistPage(bookId, pageKey, async () => {
    const snap = await getDoc(pageRef(bookId, pageKey))
    return snap.exists() ? (snap.data() as ChecklistBookPage) : null
  })
}

export const listPages = async (bookId: BookId): Promise<ChecklistBookPage[]> => {
  const snap = await getDocs(query(pagesCollectionRef(bookId), orderBy('pageKey', 'asc')))
  return snap.docs.map((d) => {
    const page = d.data() as ChecklistBookPage
    primeChecklistPageCache(bookId, page.pageKey, page)
    return page
  })
}

/**
 * Create a page at `pageKey` if it doesn't exist. Adopts the versionId from
 * the nearest prior page (or the book's active version) via
 * layoutVersionService.resolveInheritedVersion.
 */
export const ensurePage = async (
  bookId: BookId,
  workspaceId: string,
  type: ChecklistType,
  pageKey: PageKey,
  dateIso: string,
  actor: BookActor,
  opts: {
    ensureLayoutVersion?: boolean
    ensureDetailVersion?: boolean
  } = {},
): Promise<ChecklistBookPage> => {
  const shouldEnsureLayoutVersion = opts.ensureLayoutVersion !== false
  const shouldEnsureDetailVersion = opts.ensureDetailVersion !== false
  const existing = await getPage(bookId, pageKey)
  if (existing) {
    const needsLayoutVersion = shouldEnsureLayoutVersion && !existing.layoutVersionId
    const needsDetailVersion = shouldEnsureDetailVersion && !existing.detailVersionId
    if (!needsLayoutVersion && !needsDetailVersion) return existing

    const layoutResolution = needsLayoutVersion
      ? await resolveInheritedVersion(bookId, pageKey)
      : null
    const detailResolution = needsDetailVersion
      ? await resolveInheritedDetailVersion(bookId, pageKey)
      : null

    const layoutVersionId = !needsLayoutVersion
      ? existing.layoutVersionId
      : !layoutResolution || layoutResolution.kind === 'none'
        ? await ensureActiveLayoutVersion(bookId, workspaceId, actor)
        : layoutResolution.versionId
    const detailVersionId = !needsDetailVersion
      ? existing.detailVersionId
      : !detailResolution || detailResolution.kind === 'none'
        ? await ensureActiveDetailVersion(bookId, workspaceId, actor)
        : detailResolution.versionId

    const updates: Record<string, unknown> = {
      updatedAt: serverTimestamp(),
      lastUpdatedBy: actor.uid,
      lastUpdatedByName: actor.displayName ?? null,
    }
    if (needsLayoutVersion) updates.layoutVersionId = layoutVersionId
    if (needsDetailVersion) updates.detailVersionId = detailVersionId

    await setDoc(pageRef(bookId, pageKey), updates, { merge: true })

    const nextPage = {
      ...existing,
      ...(needsLayoutVersion ? { layoutVersionId } : {}),
      ...(needsDetailVersion ? { detailVersionId } : {}),
    }
    primeChecklistPageCache(bookId, pageKey, nextPage)
    return nextPage
  }

  const resolution = await resolveInheritedVersion(bookId, pageKey)
  let layoutVersionId: string
  if (resolution.kind === 'none') {
    layoutVersionId = await ensureActiveLayoutVersion(bookId, workspaceId, actor)
  } else {
    layoutVersionId = resolution.versionId
  }
  const detailResolution = await resolveInheritedDetailVersion(bookId, pageKey)
  let detailVersionId: string
  if (detailResolution.kind === 'none') {
    detailVersionId = await ensureActiveDetailVersion(bookId, workspaceId, actor)
  } else {
    detailVersionId = detailResolution.versionId
  }

  // Derive parentContainerId at bootstrap:
  // pages don't have a parent container of their own — they represent the
  // book's canvas. But rules queries may still filter by parentContainerId;
  // we denorm the *book id* as a stable synthetic parent so downstream
  // filters remain intentional.
  const page: ChecklistBookPage = {
    id: pageKey,
    bookId,
    workspaceId,
    checklistType: type,
    pageKey,
    dateIso,
    layoutVersionId,
    detailVersionId,
    detailPatch: {},
    completions: {},
    totalNodes: 0,
    completedNodes: 0,
    createdAt: serverTimestamp(),
    createdById: actor.uid,
    createdByName: actor.displayName ?? null,
    updatedAt: serverTimestamp(),
    lastUpdatedBy: actor.uid,
    lastUpdatedByName: actor.displayName ?? null,
    recordVersion: 1,
  }
  await setDoc(pageRef(bookId, pageKey), page)
  primeChecklistPageCache(bookId, pageKey, page)
  return page
}

export const ensurePageForKey = async (
  bookId: BookId,
  workspaceId: string,
  type: ChecklistType,
  pageKey: PageKey,
  actor: BookActor,
): Promise<ChecklistBookPage> =>
  ensurePage(
    bookId,
    workspaceId,
    type,
    pageKey,
    pageDateIsoFromPageKey(type, pageKey),
    actor,
  )

export const ensurePageDetailVersionForKey = async (
  bookId: BookId,
  workspaceId: string,
  type: ChecklistType,
  pageKey: PageKey,
  actor: BookActor,
): Promise<ChecklistBookPage> =>
  ensurePage(
    bookId,
    workspaceId,
    type,
    pageKey,
    pageDateIsoFromPageKey(type, pageKey),
    actor,
    { ensureLayoutVersion: false, ensureDetailVersion: true },
  )

// ---------------------------------------------------------------------------
// Lookahead materialization
// ---------------------------------------------------------------------------

/**
 * Ensure the window of `lookaheadPages` pages starting from today exists.
 * Runs in a single batched write. Skips pages that already exist.
 */
export const materializeLookahead = async (
  bookId: BookId,
  workspaceId: string,
  type: RecurringChecklistType,
  actor: BookActor,
  opts: { lookaheadPages: number; today?: Date } = { lookaheadPages: DEFAULT_LOOKAHEAD[type as RecurringChecklistType] },
): Promise<PageKey[]> => {
  const today = opts.today ?? new Date()
  const keys = enumerateLookaheadPageKeys(type, opts.lookaheadPages, today)

  // Bulk read existing pages so we only ensure the missing ones.
  const existing = await Promise.all(keys.map((k) => getPage(bookId, k)))
  const missingKeys: PageKey[] = []
  const missingDates: string[] = []
  keys.forEach((k, i) => {
    if (!existing[i]) {
      missingKeys.push(k)
      missingDates.push(pageDateIsoFor(type, advanceDate(type, today, i)))
    }
  })

  const created: PageKey[] = []
  for (let i = 0; i < missingKeys.length; i += 1) {
    // ensurePage does its own inheritance walk per page — sequential so
    // each new page sees the previous one's version.
    await ensurePage(bookId, workspaceId, type, missingKeys[i], missingDates[i], actor)
    created.push(missingKeys[i])
  }
  return created
}

// ---------------------------------------------------------------------------
// Completion writes
// ---------------------------------------------------------------------------

/**
 * Set a checklist's completion state on a specific page. Field-level merges
 * into the inline `completions[checklistId]` entry — passing only
 * `{ proofFileUrl }` will not clobber the entry's existing `isCompleted` /
 * `workflowStatus`. Every non-`undefined` key in `patch` overwrites; missing
 * keys are preserved from the previous entry. Updates aggregate counters.
 *
 * Mirrors the server-side merge shape used by
 * `mirrorDefinitionCompletionToPage` and `writeChecklistUpdateServer` so
 * client and cloud writers converge on the same completion doc shape.
 */
import { isRepeatPageStateKey } from './repeat/completions'

export const setChecklistCompletionOnPage = async (
  bookId: BookId,
  pageKey: PageKey,
  checklistId: string,
  patch: Partial<ChecklistCompletionState>,
  actor: BookActor,
  opts?: { helperTarget?: boolean },
): Promise<void> => {
  const isDailyOrWeekly = bookId.endsWith('_DAILY') || bookId.endsWith('_WEEKLY')
  if (isDailyOrWeekly) {
    const hasState = Object.keys(patch).some((k) => isRepeatPageStateKey(k))
    if (hasState) {
      throw new Error('Daily/Weekly status changes go through updateChecklistWorkflowStatus')
    }
    if (Object.keys(patch).length > 0) {
      const { writeRepeatPageEntry } = await import('./repeatPageService')
      await writeRepeatPageEntry({
        checklistId,
        periodKey: pageKey,
        fields: patch as Record<string, unknown>,
      })
    }
    return
  }
  let page = await getPage(bookId, pageKey)
  if (!page) {
    // Page doesn't exist yet — auto-bootstrap it. Derive workspaceId + type
    // from the definition (which every completion write implies exists) so
    // we can call ensurePage safely without extra caller plumbing.
    const defSnap = await getDoc(doc(firestoreDb, `${CHECKLISTS_COL}/${checklistId}`))
    if (!defSnap.exists()) {
      throw new Error(`setChecklistCompletionOnPage: no definition ${checklistId} to derive workspace/type from`)
    }
    const def = defSnap.data() as { workspaceId?: string; checklistType?: ChecklistType }
    if (!def.workspaceId) {
      throw new Error(`setChecklistCompletionOnPage: definition ${checklistId} missing workspaceId`)
    }
    const type = def.checklistType ?? 'INDIVIDUAL'
    await ensurePageForKey(bookId, def.workspaceId, type, pageKey, actor)
    page = await getPage(bookId, pageKey)
    if (!page) {
      // Rules or race — bail with a clear signal rather than silently
      // corrupting the counter math on undefined data.
      throw new Error(`setChecklistCompletionOnPage: page ${pageKey} could not be created in book ${bookId}`)
    }
  }
  const previous = page.completions?.[checklistId] ?? {}

  const nextEntry: ChecklistCompletionState = {
    // Default `isCompleted` to false so brand-new entries have the required
    // field. Overridden by anything in `previous` or `patch`.
    ...previous,
    ...patch,
    isCompleted: patch.isCompleted ?? previous.isCompleted ?? false,
  }

  const merged: ChecklistBookPage['completions'] = {
    ...(page.completions ?? {}),
    [checklistId]: nextEntry,
  }

  const completedNodes = Object.values(merged).filter((c) => c.isCompleted).length
  const totalNodes = Object.keys(merged).length

  await updateDoc(pageRef(bookId, pageKey), {
    completions: merged,
    completedNodes,
    totalNodes,
    ...(opts?.helperTarget ? { helperTargetNodeId: checklistId } : {}),
    updatedAt: serverTimestamp(),
    lastUpdatedBy: actor.uid,
    lastUpdatedByName: actor.displayName ?? null,
    recordVersion: (page.recordVersion ?? 0) + 1,
  })
  primeChecklistPageCache(bookId, pageKey, {
    ...page,
    completions: merged,
    completedNodes,
    totalNodes,
    lastUpdatedBy: actor.uid,
    lastUpdatedByName: actor.displayName ?? null,
    recordVersion: (page.recordVersion ?? 0) + 1,
  })
}

/**
 * Remove a checklist entirely from a page's completions map (e.g. after
 * archival). Idempotent.
 */
export const removeChecklistFromPage = async (
  bookId: BookId,
  pageKey: PageKey,
  checklistId: string,
  actor: BookActor,
): Promise<void> => {
  const isDailyOrWeekly = bookId.endsWith('_DAILY') || bookId.endsWith('_WEEKLY')
  if (isDailyOrWeekly) {
    const { writeRepeatPageEntry } = await import('./repeatPageService')
    await writeRepeatPageEntry({
      checklistId,
      periodKey: pageKey,
      op: 'REMOVE',
    })
    return
  }
  const page = await getPage(bookId, pageKey)
  if (!page) return
  const merged = { ...(page.completions ?? {}) }
  if (!(checklistId in merged)) return
  delete merged[checklistId]

  const completedNodes = Object.values(merged).filter((c) => c.isCompleted).length
  const totalNodes = Object.keys(merged).length

  await updateDoc(pageRef(bookId, pageKey), {
    completions: merged,
    completedNodes,
    totalNodes,
    updatedAt: serverTimestamp(),
    lastUpdatedBy: actor.uid,
    lastUpdatedByName: actor.displayName ?? null,
    recordVersion: (page.recordVersion ?? 0) + 1,
  })
  primeChecklistPageCache(bookId, pageKey, {
    ...page,
    completions: merged,
    completedNodes,
    totalNodes,
    lastUpdatedBy: actor.uid,
    lastUpdatedByName: actor.displayName ?? null,
    recordVersion: (page.recordVersion ?? 0) + 1,
  })
}

// ---------------------------------------------------------------------------
// Retention prune
// ---------------------------------------------------------------------------

/**
 * Delete pages older than `retentionDays` from today. Never touches the
 * INDIVIDUAL book. Returns the list of pruned page keys.
 */
export const pruneOldPages = async (
  workspaceId: string,
  type: RecurringChecklistType,
  retentionDays: number,
  today: Date = new Date(),
): Promise<PageKey[]> => {
  if (retentionDays <= 0) return []
  const cutoff = advanceDate(type, today, -Math.abs(retentionDays))
  const cutoffKey = pageKeyFor(type, cutoff)

  const bookId = bookIdFor(workspaceId, type)
  const oldQ = query(
    pagesCollectionRef(bookId),
    where('pageKey', '<', cutoffKey),
    orderBy('pageKey', 'asc'),
  )
  const snap = await getDocs(oldQ)
  if (snap.empty) return []

  const batch = writeBatch(firestoreDb)
  const removed: PageKey[] = []
  snap.docs.forEach((d) => {
    batch.delete(d.ref)
    const page = d.data() as ChecklistBookPage
    removed.push(page.pageKey)
  })
  await batch.commit()
  removed.forEach((pageKey) => invalidateChecklistPageCache(bookId, pageKey))
  return removed
}

// ---------------------------------------------------------------------------
// Definitions listing (used by canvas load + breadcrumb filter)
// ---------------------------------------------------------------------------

export const listDefinitionsForBook = async (
  workspaceId: string,
  type: ChecklistType,
): Promise<ChecklistDefinition[]> => {
  return readCachedChecklistDefinitions(workspaceId, type, async () => {
    const q = query(
      checklistsCollectionRef(),
      where('workspaceId', '==', workspaceId),
      where('checklistType', '==', type),
      ...scopedReadConstraints(workspaceId),
    )
    const snap = await getDocs(q)
    return snap.docs
      .map((d) => d.data() as ChecklistDefinition)
      .filter((def) => !def.isArchived)
  })
}

// ---------------------------------------------------------------------------
// Subscriptions
// ---------------------------------------------------------------------------

/** Subscribe to a single page (for the currently-visible date on the canvas). */
export const subscribeToPage = (
  bookId: BookId,
  pageKey: PageKey,
  onChange: (page: ChecklistBookPage | null) => void,
): Unsubscribe =>
  onSnapshot(pageRef(bookId, pageKey), (snap) => {
    const page = snap.exists() ? (snap.data() as ChecklistBookPage) : null
    primeChecklistPageCache(bookId, pageKey, page)
    onChange(page)
  }, (error) => {
    // Bumped from debug → warn so subscription failures (permission-denied,
    // network) are visible in the console. Consumer still receives null,
    // which they treat as "no page yet"; the warn line is the diagnostic
    // signal that lets us tell "no page" from "denied".
    console.warn('[checklistBookService] page subscription failed', bookId, pageKey, (error as Error)?.message)
    onChange(null)
  })

/** Subscribe to every definition belonging to a book. */
export const subscribeToBookDefinitions = (
  workspaceId: string,
  type: ChecklistType,
  onChange: (defs: ChecklistDefinition[]) => void,
): Unsubscribe => {
  const q = query(
    checklistsCollectionRef(),
    where('workspaceId', '==', workspaceId),
    where('checklistType', '==', type),
    ...scopedReadConstraints(workspaceId),
  )

  return onSnapshot(q, (snap) => {
    const definitions = snap.docs
      .map((d) => d.data() as ChecklistDefinition)
      .filter((def) => !def.isArchived)
    primeChecklistDefinitionListCache(workspaceId, type, definitions)
    onChange(definitions)
  }, (error) => {
    console.debug('[checklistBookService] definition subscription failed', workspaceId, type, (error as Error)?.message)
    onChange([])
  })
}

export const subscribeToBookPages = (
  bookId: BookId,
  onChange: (pages: ChecklistBookPage[]) => void,
): Unsubscribe =>
  onSnapshot(
    query(pagesCollectionRef(bookId), orderBy('pageKey', 'asc')),
    (snap) => {
      const pages = snap.docs.map((d) => {
        const page = d.data() as ChecklistBookPage
        primeChecklistPageCache(bookId, page.pageKey, page)
        return page
      })
      onChange(pages)
    },
    () => {
      onChange([])
    },
  )

// ---------------------------------------------------------------------------
// Settings integration
// ---------------------------------------------------------------------------

/** Look up the workspace-wide lookahead window from
 *  WorkspaceRecurringSettings, falling back to the DEFAULT_LOOKAHEAD map. */
export const resolveLookaheadForType = (
  settings: WorkspaceRecurringSettings | null | undefined,
  type: RecurringChecklistType,
): number => {
  void settings
  void type
  return normalizeRecurringWindowDays(DEFAULT_RECURRING_WINDOW_DAYS)
}

/**
 * Parent-scoped recurring refactor — pages are lazy now, so this reduces
 * to bootstrapping the DAILY and WEEKLY book docs (so ticks/create can
 * write into them) with no forward window. Kept for callers already wired.
 */
export const materializeRecurringBookLookahead = async (
  workspaceId: string,
  settings: WorkspaceRecurringSettings | null | undefined,
  actor: BookActor,
): Promise<void> => {
  void settings
  for (const type of ['DAILY', 'WEEKLY'] as const satisfies ReadonlyArray<RecurringChecklistType>) {
    await ensureBook(workspaceId, type, actor)
  }
}
